import React, { FC, useState } from 'react';
import { 
  LifeBuoy, 
  Check, 
  Clock, 
  Sparkles, 
  Send, 
  RefreshCw,
  X,
  HelpCircle
} from 'lucide-react';
import { SupportTicket } from '../types';

interface SupportViewProps {
  tickets: SupportTicket[];
  onResolve: (id: string) => void;
  onSendReply: (id: string, message: string) => void;
}

export const SupportView: FC<SupportViewProps> = ({ tickets, onResolve, onSendReply }) => {
  const [filter, setFilter] = useState<'all' | 'open' | 'resolved'>('open');
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(
    tickets.length > 0 ? (tickets.find(t => t.status === 'open')?.id || tickets[0].id) : null
  );
  const [replyMessage, setReplyMessage] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [aiDraft, setAiDraft] = useState('');
  
  const filteredTickets = tickets.filter(t => {
    if (filter === 'all') return true;
    return t.status === filter;
  });

  const selectedTicket = tickets.find(t => t.id === selectedTicketId) || filteredTickets[0];

  const handleGenerateAiDraft = async (ticketId: string) => {
    setIsGenerating(true);
    setAiDraft('');
    try {
      const res = await fetch('/api/tickets/generate-draft', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ticketId }),
      });
      const data = await res.json();
      if (data.draft) {
        setAiDraft(data.draft);
        setReplyMessage(data.draft); // Auto fill reply field
      }
    } catch (err) {
      console.error(err);
      setAiDraft('Süni İntellekt modeli ilə əlaqə qurmaq mümkün olmadı. Zəhmət olmasa API açarınızı yoxlayın.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSendReplySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTicket || !replyMessage.trim()) return;
    onSendReply(selectedTicket.id, replyMessage);
    setReplyMessage('');
    setAiDraft('');
  };

  // Pre-configured Quick Snippet Templates
  const templates = [
    {
      title: 'Hosting DNS yönləndirilməsi',
      body: 'DNS qeydləri adətən 2-24 saat ərzində yenilənir. Domenizin statusu yoxlanıldı, A-qeydi tamamilə doğrudur. SSL sertifikatı yaxın 1 saat ərzində sistem tərəfindən avtomatik generasiya ediləcək.'
    },
    {
      title: 'Şəkillərin sıxılması və CDN',
      body: 'Siz Restoran İdarəetmə panelindən "Medyaların avtomatik sıxılması" seçimini aktiv etməlisiniz. Bizim CDN sistemi tərəfindən şəkillərin ölçüləri avtomatik olaraq 70%-ə qədər sıxılacaq və sayt sürəti artacaq.'
    },
    {
      title: 'IIKO / R-Keeper inteqrasiyası',
      body: 'Menyunuzu IIKO kassa proqramı ilə sinxronizasiya etmək üçün, lütfən öz IIKO Cloud idarəetmə panelinizdən API açarını qeyd edib bizə göndərin. Sizin profilinizə addım-addım PDF təlimatı əlavə etdik.'
    }
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      
      {/* Title Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="font-display font-medium text-2xl tracking-tight text-slate-900">
            Müştəri Dəstəyi Kabineti
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Yaranmış nasazlıqların aradan qaldırılması və tərəfdaşların suallarının Gemini AI vasitəsilə bilet formasında həlli.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-mono font-bold bg-rose-50 text-rose-700 px-2.5 py-1 rounded-md border border-rose-200 animate-pulse">
            Açıq müraciətlər: {tickets.filter(t => t.status === 'open').length}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        
        {/* Left column: Ticket List */}
        <div className="lg:col-span-5 bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-xs">
          {/* List Header controls */}
          <div className="p-4 bg-slate-50 border-b border-slate-200 flex items-center justify-between gap-2">
            <div className="flex bg-slate-200/60 p-1 rounded-lg w-full">
              <button
                type="button"
                onClick={() => setFilter('open')}
                className={`flex-1 py-1.5 text-center text-xs font-semibold rounded-md transition-all cursor-pointer select-none ${
                  filter === 'open' ? 'bg-white text-slate-900 shadow-xs' : 'text-slate-500 hover:text-slate-900'
                }`}
              >
                Açıq olanlar
              </button>
              <button
                type="button"
                onClick={() => setFilter('resolved')}
                className={`flex-1 py-1.5 text-center text-xs font-semibold rounded-md transition-all cursor-pointer select-none ${
                  filter === 'resolved' ? 'bg-white text-slate-900 shadow-xs' : 'text-slate-500 hover:text-slate-900'
                }`}
              >
                Həll edilmişlər
              </button>
              <button
                type="button"
                onClick={() => setFilter('all')}
                className={`flex-1 py-1.5 text-center text-xs font-semibold rounded-md transition-all cursor-pointer select-none ${
                  filter === 'all' ? 'bg-white text-slate-900 shadow-xs' : 'text-slate-500 hover:text-slate-900'
                }`}
              >
                Hamısı
              </button>
            </div>
          </div>

          {/* Tickets checklist container */}
          <div className="divide-y divide-slate-100 max-h-[580px] overflow-y-auto font-sans">
            {filteredTickets.length === 0 ? (
              <div className="p-8 text-center text-slate-400 text-xs italic">
                Bu kateqoriyada müraciət yoxdur.
              </div>
            ) : (
              filteredTickets.map(ticket => {
                const categoryColors = 
                  ticket.category === 'Billing' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' :
                  ticket.category === 'Technical' ? 'bg-blue-50 text-blue-700 border-blue-100' :
                  ticket.category === 'Feature Request' ? 'bg-purple-50 text-purple-700 border-purple-100' :
                  'bg-indigo-50 text-indigo-700 border-indigo-100';

                const isSelected = selectedTicket?.id === ticket.id;

                return (
                  <button
                    key={ticket.id}
                    onClick={() => {
                      setSelectedTicketId(ticket.id);
                      setAiDraft('');
                    }}
                    className={`w-full text-left p-4 hover:bg-slate-50/50 transition-all block cursor-pointer outline-none ${
                      isSelected ? 'bg-slate-50 border-r-4 border-indigo-600' : ''
                    }`}
                  >
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between gap-2">
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded border ${categoryColors}`}>
                          {ticket.category === 'Billing' ? 'Ödəniş / Billing' :
                           ticket.category === 'Technical' ? 'Texniki' :
                           ticket.category === 'Feature Request' ? 'Təkliflər' : 'Domen qoşulması'}
                        </span>
                        
                        <div className="flex items-center gap-1 text-[10px] font-mono text-slate-400">
                          {ticket.status === 'open' ? (
                            <Clock className="w-3.5 h-3.5 text-amber-500" />
                          ) : (
                            <Check className="w-3.5 h-3.5 text-emerald-500" />
                          )}
                          <span>{new Date(ticket.createdAt).toLocaleDateString()}</span>
                        </div>
                      </div>

                      <h4 className="font-bold text-slate-800 text-xs truncate leading-snug">
                        {ticket.subject}
                      </h4>
                      <p className="text-[11px] text-slate-500 truncate mt-0.5">
                        {ticket.restaurantName} (ID: {ticket.restaurantId})
                      </p>
                      
                      <p className="text-[11px] text-slate-400 line-clamp-1 italic">
                        "{ticket.message}"
                      </p>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* Right column: Ticket Detailed view & Action center */}
        <div className="lg:col-span-7 bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-xs min-h-[500px] flex flex-col justify-start">
          {selectedTicket ? (
            <div className="flex-1 flex flex-col justify-between">
              
              {/* Ticket Metadata Header */}
              <div className="p-6 border-b border-slate-150 bg-slate-50/50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-mono font-bold bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded">
                      Müraciət ID: #{selectedTicket.id}
                    </span>
                    <span className={`text-[10px] font-bold px-2 py-0.2 rounded-full border ${
                      selectedTicket.status === 'open' 
                        ? 'bg-amber-100 text-amber-800 border-amber-200' 
                        : 'bg-emerald-100 text-emerald-800 border-emerald-200'
                    }`}>
                      {selectedTicket.status === 'open' ? 'Cavab gözləyir' : 'Həll edilib'}
                    </span>
                  </div>
                  <h3 className="font-display font-black text-lg text-slate-900 mt-1">
                    {selectedTicket.subject}
                  </h3>
                  <p className="text-xs text-slate-500">
                    Müraciət edən: <span className="font-bold text-slate-700">{selectedTicket.restaurantName}</span>
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => onResolve(selectedTicket.id)}
                  className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
                    selectedTicket.status === 'open'
                      ? 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm'
                      : 'bg-slate-200 hover:bg-slate-300 text-slate-750'
                  }`}
                >
                  <Check className="w-4 h-4" />
                  <span>{selectedTicket.status === 'open' ? 'Bileti bağla' : 'Yenidən aç'}</span>
                </button>
              </div>

              {/* Message Details Wrapper */}
              <div className="p-6 space-y-6 flex-1">
                {/* Outgoing Restaurant client query */}
                <div className="bg-slate-50 border border-slate-100 p-4 rounded-xl space-y-3 relative">
                  <div className="flex justify-between items-center border-b border-slate-200/50 pb-2">
                    <span className="text-[10px] font-mono font-bold text-slate-400">RESTORANDAN GƏLƏN MƏKTUB:</span>
                    <span className="text-[10px] text-slate-400 font-mono">
                      {new Date(selectedTicket.createdAt).toLocaleString()}
                    </span>
                  </div>
                  <p className="text-xs text-slate-700 leading-relaxed font-sans whitespace-pre-wrap">
                    "{selectedTicket.message}"
                  </p>
                </div>

                {/* AI Assistant Quick Draft Section */}
                {selectedTicket.status === 'open' && (
                  <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-4 rounded-xl border border-indigo-100/60 space-y-3 relative">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5 text-indigo-800">
                        <Sparkles className="w-4 h-4 text-indigo-600" />
                        <span className="text-xs font-bold uppercase font-mono tracking-wide">
                          Gemini AI Cavab Layihəsi
                        </span>
                      </div>
                      
                      <button
                        type="button"
                        id="btn-gemini-draft"
                        onClick={() => handleGenerateAiDraft(selectedTicket.id)}
                        disabled={isGenerating}
                        className="px-2.5 py-1 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white rounded-md text-[10px] font-bold transition-all flex items-center gap-1 cursor-pointer select-none"
                      >
                        {isGenerating ? (
                          <>
                            <RefreshCw className="w-3 h-3 animate-spin" />
                            <span>Hazırlanır...</span>
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-3.5 h-3.5" />
                            <span>Süni İntellektlə cavab yaz</span>
                          </>
                        )}
                      </button>
                    </div>

                    {isGenerating && (
                      <div className="space-y-2 py-4 animate-pulse animate-duration-1000">
                        <div className="h-3 bg-indigo-250/20 rounded-full w-3/4" />
                        <div className="h-3 bg-indigo-250/20 rounded-full w-5/6" />
                        <div className="h-3 bg-indigo-250/20 rounded-full w-2/3" />
                      </div>
                    )}

                    {aiDraft && (
                      <div className="text-xs text-slate-700 bg-white/80 p-3 rounded-lg border border-indigo-200/30 font-mono text-[11px] leading-relaxed max-h-48 overflow-y-auto w-full whitespace-pre-line shadow-xs">
                        {aiDraft}
                      </div>
                    )}

                    {!aiDraft && !isGenerating && (
                      <p className="text-[11px] text-indigo-900/60 font-sans leading-relaxed">
                        Sağ tərəfdəki düyməni sıxaraq modelin partnyora uyğun (tarifi, fəaliyyəti və skan limiti nəzərə alınmaqla) mükəmməl professional cavab hazırlamasını təmin edə bilərsiniz.
                      </p>
                    )}
                  </div>
                )}

                {/* Submitting Center Form / Reply State */}
                {selectedTicket.status === 'open' ? (
                  <form onSubmit={handleSendReplySubmit} className="space-y-4">
                    <div className="space-y-1.5">
                      <label className="text-[11px] font-bold text-slate-600 uppercase font-mono">
                        Rəsmi cavabın mətni
                      </label>
                      <textarea
                        rows={4}
                        required
                        placeholder="Bura fərdi cavab yazın və ya süni intellektin hazırladığı şablonu düzəldin..."
                        value={replyMessage}
                        onChange={(e) => setReplyMessage(e.target.value)}
                        className="w-full border border-slate-200 p-3 text-xs rounded-xl focus:outline-hidden focus:border-indigo-500 font-sans"
                      />
                    </div>

                    {/* Quick answers inserts helper */}
                    <div className="space-y-1.5">
                      <span className="text-[10px] font-mono font-bold text-slate-400 uppercase block">Sürətli şablon cümlələr</span>
                      <div className="flex flex-wrap gap-1.5 animate-fade-in">
                        {templates.map((tpl, i) => (
                          <button
                            key={i}
                            type="button"
                            onClick={() => {
                              const curr = replyMessage ? replyMessage + '\n\n' : '';
                              setReplyMessage(curr + tpl.body);
                            }}
                            className="bg-slate-50 hover:bg-slate-100 border border-slate-200 px-2 py-1 rounded text-[10px] text-slate-650 font-medium transition-all cursor-pointer select-none"
                          >
                            + {tpl.title}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="flex justify-between items-center pt-3 border-t border-slate-100">
                      <span className="text-[11px] text-slate-450 font-mono">
                        "Cavabı Göndər" düyməsini sıxmaq müraciəti tam bağlayacaq
                      </span>
                      <button
                        type="submit"
                        className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold font-sans flex items-center gap-1.5 transition-all shadow-md shadow-indigo-600/10 cursor-pointer select-none"
                      >
                        <Send className="w-3.5 h-3.5" />
                        <span>Cavabı göndər</span>
                      </button>
                    </div>
                  </form>
                ) : (
                  <div className="bg-emerald-50/50 border border-emerald-100 p-5 rounded-xl flex items-center justify-between animate-fade-in">
                    <div>
                      <h4 className="font-bold text-emerald-800 text-xs">Müraciət tamamilə uğurla həll edildi və bağlandı</h4>
                      <p className="text-[11px] text-emerald-600 mt-1">Müştəri razı qaldı. Söhbət arxiv bölməsinə köçürüldü.</p>
                    </div>
                    <Check className="w-8 h-8 text-emerald-500 shrink-0" />
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center p-8 text-center space-y-4">
              <LifeBuoy className="w-12 h-12 text-slate-300" />
              <div>
                <p className="font-semibold text-slate-600 text-sm">Soldakı siyahıdan müraciət seçin</p>
                <p className="text-xs text-slate-400 mt-1">Siz bura daxil olan biletin təfərrüatlarına baxa, süni intellekt və sürətli şablonlardan yararlana bilərsiniz.</p>
              </div>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};
